<template>
    <aside class="nav-new-drawer">
        <header class="drawer-header">
            <div class="drawer-title-group">
                <Icon :name="navItem?.icon?.name" class="drawer-title-icon" size="md" />
                <h2 class="drawer-title">{{ navItem?.label }}</h2>
            </div>
            <button class="drawer-close-btn" @click="$emit('close')" title="Close sidebar">
                <Icon name="chevron-left" />
            </button>
        </header>
        <div class="drawer-content">
            <button class="drawer-nav-btn" @click="$emit('commit', navItem)">
                {{ navItem?.label }}
            </button>
            <p class="drawer-section-header" v-show="hasGridControls && showGridControls">Grid Controls</p>
            <div id="nav-drawer-grid-controls" ref="gridControlsRef" v-show="showGridControls"></div>
            <slot name="drawer-content"></slot>
        </div>
    </aside>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Icon from '@common/icon/Icon.vue';

defineProps({
    navItem: {
        type: Object,
        default: null
    },
    showGridControls: {
        type: Boolean,
        default: true,
    },
});

defineEmits(['close', 'commit']);

const gridControlsRef = ref(null);
const hasGridControls = ref(false);
let _mutationObserver = null;

onMounted(() => {
    _mutationObserver = new MutationObserver(() => {
        hasGridControls.value = (gridControlsRef.value?.childNodes.length ?? 0) > 0;
    });
    if (gridControlsRef.value) {
        _mutationObserver.observe(gridControlsRef.value, { childList: true });
    }
});

onUnmounted(() => {
    _mutationObserver?.disconnect();
});
</script>

<style scoped>
.nav-new-drawer {
    width: var(--nav-panel-width);
    height: 100%;
    display: flex;
    flex-direction: column;
    animation: slideIn var(--transition-default) ease-out;
}

.drawer-nav-btn {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: var(--text-color-body);
    border-radius: var(--radius-xs);
    transition: background-color var(--transition-default) ease;
}

.drawer-nav-btn:hover {
    background-color: var(--surface-subtle);
}

.drawer-nav-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

.drawer-section-header {
    margin: var(--space-6) 0 var(--space-2);
    padding: 0 var(--space-5);
    font-size: var(--text-size-base);
    font-weight: var(--text-weight-light);
    color: var(--text-color-caption);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

:deep(.drawer-nav-button button) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: var(--text-color-body);
    border-radius: var(--radius-xs);
    transition: background-color var(--transition-default) ease;
    box-shadow: none;
}

:deep(.drawer-nav-button button:hover) {
    background-color: var(--surface-subtle);
    box-shadow: none;
}

:deep(.drawer-nav-button button:focus-visible) {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

.drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-5) var(--space-6) var(--space-6);
    min-height: 0;
}

.drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-5) var(--space-6);
    border-bottom: 1px solid var(--border-color-default);
}

.drawer-title-group {
    display: inline-flex;
    align-items: center;
    gap: var(--space-4);
}

.drawer-title-icon {
    color: var(--text-color-subtitle);
}

.drawer-title {
    margin: 0;
    font-size: var(--text-size-base);
    font-weight: var(--text-weight-medium);
    color: var(--text-color-title);
}

.drawer-close-btn {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--text-color-subtitle);
    border-radius: var(--radius-full);
    width: var(--space-7);
    height: var(--space-7);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color var(--transition-default) ease, color var(--transition-default) ease;
}

.drawer-close-btn:hover {
    background-color: var(--surface-subtle);
    color: var(--text-color-body);
}

.drawer-close-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

@keyframes slideIn {
    from {
        transform: translateX(calc(-1 * var(--space-4)));
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

:deep(.drawer-nav-button button span::before) {
    margin-left: var(--space-1);
}
</style>
